import { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    getUStoreItems,
    createUStoreItem,
    requestUStorePurchase,
    uploadUStoreImage,
    uploadFile,
} from '@/lib/api';
import { getImageFileFromClipboard, insertTextAtCursor } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';

type StoreItem = {
    item_id: number;
    title: string;
    description: string;
    image_url?: string | null;
    points_cost: number;
    is_sold: boolean;
    creator_id: number;
    creator_name?: string | null;
    creator_role?: string | null;
    created_at: string;
};

export function UStore() {
    const [items, setItems] = useState<StoreItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const [points, setPoints] = useState('0');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // @ts-ignore
    const { theme, user } = useOutletContext<{ theme: any; user: any }>();

    const canManage = Boolean(user?.ustore_enabled);

    const fetchItems = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getUStoreItems();
            setItems(res.data || []);
        } catch (err) {
            console.error(err);
            setError('商品一覧の取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user?.unito_enabled && !canManage) return;
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        autoResizeTextarea(descriptionRef.current);
    }, [description]);

    const handleCreate = async () => {
        if (!title.trim() || !description.trim()) {
            setError('タイトルと詳細を入力してください。');
            return;
        }

        const cost = Number(points);
        if (Number.isNaN(cost) || cost <= 0) {
            setError('ポイント数は1以上で入力してください。');
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');
        try {
            let image_url: string | null | undefined = undefined;
            if (imageFile) {
                const upload = await uploadUStoreImage(imageFile);
                image_url = upload.data?.url;
            }

            await createUStoreItem({
                title: title.trim(),
                description: description.trim(),
                points_cost: cost,
                image_url,
            });

            setTitle('');
            setDescription('');
            setPoints('0');
            setImageFile(null);
            setSuccess('出品が完了しました。');
            fetchItems();
        } catch (err: any) {
            console.error(err);
            setError(err?.response?.data?.detail || '出品に失敗しました。');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRequest = async (itemId: number) => {
        setError('');
        setSuccess('');
        try {
            await requestUStorePurchase(itemId);
            setSuccess('購入申請を送信しました。謁見の間に通知されます。');
        } catch (err: any) {
            console.error(err);
            setError(err?.response?.data?.detail || '購入申請に失敗しました。');
        }
    };

    const handlePasteDescriptionImage = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        try {
            const res = await uploadFile(imageFile);
            const url = res.data?.url;
            if (url) {
                insertTextAtCursor(e.currentTarget, description, `\n${url}\n`, setDescription);
            }
        } catch (err) {
            console.error('Paste upload failed', err);
        }
    };

    if (!user) {
        return <div className="p-6 text-center opacity-70">Loading...</div>;
    }

    if (!user.unito_enabled && !canManage) {
        return <div className="p-6 text-center opacity-70">U-Storeは現在利用できません。</div>;
    }

    return (
        <div className="max-w-6xl mx-auto mt-8 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-serif font-bold tracking-wider" style={{ color: theme?.colors?.text }}>
                        U-Store
                    </h1>
                    <p className="text-sm opacity-70">Unitoポイントで特別なアイテムを交換できます。</p>
                </div>
                {user.unito_enabled && (
                    <div className="flex gap-3 text-sm">
                        <div className="px-4 py-2 rounded-full border" style={{ borderColor: theme?.colors?.accent, color: theme?.colors?.accent }}>
                            現在: {user.unito_points}
                        </div>
                        <div className="px-4 py-2 rounded-full border" style={{ borderColor: theme?.colors?.accent, color: theme?.colors?.accent }}>
                            総計: {user.unito_points_total ?? user.unito_points}
                        </div>
                    </div>
                )}
            </div>

            {canManage && (
                <div
                    className="p-4 rounded-xl border backdrop-blur mb-6"
                    style={{ backgroundColor: theme?.colors?.card + 'cc', borderColor: theme?.colors?.border }}
                >
                    <h2 className="font-serif font-semibold mb-4">出品する</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="タイトル"
                        />
                        <Input
                            value={points}
                            onChange={(e) => setPoints(e.target.value)}
                            placeholder="必要ポイント"
                        />
                    </div>
                    <textarea
                        ref={descriptionRef}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onInput={(e) => autoResizeTextarea(e.currentTarget)}
                        onPaste={handlePasteDescriptionImage}
                        className="w-full p-3 border rounded-lg focus:outline-none resize-none text-sm"
                        rows={4}
                        placeholder="詳細"
                        style={{ borderColor: theme?.colors?.border }}
                    />
                    <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                            className="text-sm w-full sm:w-auto"
                        />
                        <Button
                            onClick={handleCreate}
                            disabled={isSubmitting}
                            className="font-serif tracking-widest w-full sm:w-auto"
                            style={{ backgroundColor: theme?.colors?.primary, color: '#fff' }}
                        >
                            {isSubmitting ? '出品中...' : '出品する'}
                        </Button>
                    </div>
                    {imageFile && (
                        <div className="text-xs opacity-70 mt-2">選択中: {imageFile.name}</div>
                    )}
                </div>
            )}

            {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
            {success && <div className="text-green-500 text-sm mb-4">{success}</div>}

            {loading ? (
                <div className="text-center opacity-70">読み込み中...</div>
            ) : items.length === 0 ? (
                <div className="text-center opacity-60">出品はまだありません。</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((item) => {
                        const canRequest = user.unito_enabled && !item.is_sold && user.unito_points >= item.points_cost;
                        return (
                            <div
                                key={item.item_id}
                                className="p-4 rounded-xl border backdrop-blur flex gap-4"
                                style={{ backgroundColor: theme?.colors?.card + '80', borderColor: theme?.colors?.border }}
                            >
                                {item.image_url ? (
                                    <img
                                        src={item.image_url}
                                        alt={item.title}
                                        className="w-24 h-24 object-cover rounded-lg border"
                                        style={{ borderColor: theme?.colors?.border }}
                                    />
                                ) : (
                                    <div className="w-24 h-24 rounded-lg border flex items-center justify-center text-xs opacity-60"
                                        style={{ borderColor: theme?.colors?.border }}>
                                        No Image
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="flex items-start justify-between gap-2">
                                        <h3 className="font-serif font-semibold">{item.title}</h3>
                                        {item.is_sold && (
                                            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">Soldout</span>
                                        )}
                                    </div>
                                    <div className="text-xs opacity-70 mb-2">
                                        {item.creator_role ? `${item.creator_role} / ` : ''}{item.creator_name || 'Unknown'}
                                    </div>
                                    <p className="text-sm whitespace-pre-wrap mb-2">{item.description}</p>
                                    <div className="text-sm font-semibold mb-2">必要ポイント: {item.points_cost}</div>
                                    {user.unito_enabled && (
                                        <Button
                                            disabled={!canRequest}
                                            onClick={() => handleRequest(item.item_id)}
                                            className="font-serif tracking-widest"
                                            style={{ backgroundColor: theme?.colors?.primary, color: '#fff' }}
                                        >
                                            {item.is_sold ? 'Soldout' : user.unito_points < item.points_cost ? 'ポイント不足' : '購入申請'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
